import { useContext, useEffect, useRef, useState } from 'react';

import Navigation from '../../../assets/icons/trvto_navigation.png';
import ReactLoading from 'react-loading';

import './activity.css';

import { useLocation, useNavigate } from 'react-router-dom';

import TypingAnimation from '../../../components/typingAnimation/TypingAni';

import { UserAuthContext } from '../../../context/UserAuthContext';

import { debouncedHandleAutoComplete } from '../handleAutcomplete';

// Unique, non-empty names (case-insensitive), keeping the latest first
const uniqueNames = (list) => {
    const seen = new Set();
    const out = [];
    (list || []).forEach((name) => {
        const clean = String(name || '').trim();
        const k = clean.toLowerCase();
        if (!clean || seen.has(k)) return;
        seen.add(k);
        out.push(clean);
    });
    return out;
};

const recentPlacesFrom = (items) => uniqueNames((items || []).map((i) => i?.searchData?.place || i?.place));
const recentActivitiesFrom = (items) => uniqueNames((items || []).map((i) => i?.searchData?.activity));

// Hide suggestions that are already listed under "Recent searches"
const withoutRecent = (list, recent) => {
    const r = new Set(recent.map((x) => x.toLowerCase()));
    return (list || []).filter((x) => !r.has(String(x || '').trim().toLowerCase()));
};

// where the results page lives (v2 passes '/v2/search-result')
const DEFAULT_RESULTS_BASE = '/search-result';

const Activity = ({ className, style, handleIsSearch, type, resultsBase = DEFAULT_RESULTS_BASE }) => {
    const autoCompletePlaceRef = useRef();
    const autoCompleteActivityRef = useRef();

    const inputFo = useRef();
    const inputPlace = useRef();

    const [place, setPlace] = useState('');
    const [activity, setActivity] = useState('');

    const [loading, setLoading] = useState(false);

    /*
    Place suggestions

    Example:

    [
        "Jaipur",
        "Jaisalmer"
    ]
    */

    const [autoCompletePlaceData, setAutoCompletePlaceData] = useState([]);

    /*
    Activity suggestions

    Example:

    [
        "City Tour",
        "Trekking",
        "Adventure Activities"
    ]
    */

    const [autoCompleteActivityData, setAutoCompleteActivityData] = useState([]);

    // Recent places (place box empty) and recent activities (activity box empty)
    const [recentPlaces, setRecentPlaces] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);

    const saveRecentPlaces = (items) => setRecentPlaces(recentPlacesFrom(items));
    const saveRecentActivities = (items) => setRecentActivities(recentActivitiesFrom(items));

    const [spanDis, setSpanDis] = useState(true);

    const navigate = useNavigate();
    const location = useLocation();

    const { isAuthenticated } = useContext(UserAuthContext);

    const divStyle = !isAuthenticated && window.location.pathname === '/' ? { width: '100%' } : {};

    const tripText = ['Search Your Activity', 'Explore Popular Activities', 'Find Your Next Adventure'];

    /*
    ============================================================
    RESET WHEN LEAVING SEARCH PAGE
    ============================================================
    */

    useEffect(() => {
        const currentPath = location.pathname;

        const isSearchPage = currentPath.startsWith(`${resultsBase}/act`);

        if (!isSearchPage) {
            setPlace('');
            setActivity('');

            setAutoCompletePlaceData([]);
            setAutoCompleteActivityData([]);
        }
    }, [location, resultsBase]);

    /*
    ============================================================
    LOAD URL PARAMETERS
    ============================================================
    */

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        setPlace(params.get('place') || '');
        setActivity(params.get('activity') || '');
    }, [location.search]);

    /*
    ============================================================
    AUTO-FETCH ACTIVITIES + FOCUS AFTER PLACE-ONLY NAVIGATION
    ============================================================

    Whenever we land on a URL that has "place" but no "activity"
    yet (e.g. right after handlePlaceSelect navigates to
    /search-result/act?place=Jaipur to show the place's cards),
    fetch activity suggestions for that city and focus the
    activity input.

    This is driven off the URL itself rather than the ref set
    inside handlePlaceSelect, so it works correctly whether this
    component instance persists across the navigation or a fresh
    instance mounts on the results page.
    ============================================================
    */

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const urlPlace = (params.get('place') || '').trim();
        const hasActivityParam = params.has('activity');

        if (!urlPlace || hasActivityParam) {
            return;
        }

        const limit = window.innerWidth < 768 ? 5 : 10;

        debouncedHandleAutoComplete({
            type,
            place: urlPlace,
            activity: '',
            setAutoCompleteData: setAutoCompleteActivityData,
            withRecent: true,
            setRecentSearches: saveRecentActivities,
            limit
        });

        setSpanDis(false);

        setTimeout(() => {
            inputFo.current?.focus();
        }, 0);
    }, [location.search]);

    /*
    ============================================================
    AUTO FOCUS PLACE
    ============================================================
    */

    useEffect(() => {
        if (typeof handleIsSearch === 'function' && inputPlace.current) {
            inputPlace.current.focus();
        }
    }, [handleIsSearch]);

    /*
    ============================================================
    CLICK OUTSIDE
    ============================================================
    */

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (autoCompletePlaceRef.current && !autoCompletePlaceRef.current.contains(event.target)) {
                setAutoCompletePlaceData([]);
                setRecentPlaces([]);
            }

            if (autoCompleteActivityRef.current && !autoCompleteActivityRef.current.contains(event.target)) {
                setAutoCompleteActivityData([]);
                setRecentActivities([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const headerTypingAniStyle = {
        searchContainer: {
            top: 0,
            left: 0
        }
    };

    /*
    ============================================================
    ENTER
    ============================================================
    */

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    /*
    ============================================================
    PLACE CHANGE
    ============================================================

    IMPORTANT:

    We send ONLY:

    place=Jai

    We DO NOT send:

    activity=

    Therefore backend knows this is place autocomplete.
    ============================================================
    */

    const handlePlaceChange = (value) => {
        setPlace(value);

        setActivity('');

        setAutoCompleteActivityData([]);
        setRecentActivities([]);

        if (!value.trim()) {
            setAutoCompletePlaceData([]);
            return;
        }

        setRecentPlaces([]);

        const limit = window.innerWidth < 768 ? 5 : 10;

        debouncedHandleAutoComplete({
            type,
            place: value,
            setAutoCompleteData: setAutoCompletePlaceData,
            limit
        });
    };

    /*
    ============================================================
    SELECT PLACE
    ============================================================

    Selecting a place does two things:

    1. Navigates to /search-result/act?place=Jaipur so the cards
       for that place render below the search bar (handleSearch,
       act='' so only "place" is added to the URL).
    2. Moves focus to the activity input and fetches activity
       suggestions for that city, so the user can immediately
       narrow the cards by activity.

    (2) is handled by the "AUTO-FETCH ACTIVITIES + FOCUS AFTER
    PLACE-ONLY NAVIGATION" effect above, which reacts to the
    resulting URL change - that way it fires correctly even if
    this component remounts on the results page. We still try
    to focus/fetch immediately here too, for instant feedback if
    this instance survives the navigation.
    ============================================================
    */

    const handlePlaceSelect = (selected) => {
        if (!selected) return;

        setPlace(selected);
        setActivity('');

        setAutoCompletePlaceData([]);
        setAutoCompleteActivityData([]);
        setRecentPlaces([]);

        setSpanDis(false);

        setTimeout(() => {
            inputFo.current?.focus();
        }, 0);

        handleSearch(selected, '', {
            clearActivitySuggestions: false
        });
    };

    /*
    ============================================================
    PLACE INPUT FOCUS
    ============================================================

    Clicking/focusing the place field before typing anything
    shows default/trending city suggestions:

    ?place=

    Backend understands:

    "No place typed yet - give me some popular cities."

    If the user already has text in the place field, this just
    re-shows whatever suggestions already match that text - it
    won't clobber an in-progress typed search.
    ============================================================
    */

    const handlePlaceFocus = () => {
        setAutoCompleteActivityData([]);
        setRecentActivities([]);

        const limit = window.innerWidth < 768 ? 5 : 10;

        debouncedHandleAutoComplete({
            type,
            place: place.trim(),
            setAutoCompleteData: setAutoCompletePlaceData,
            // empty place box: also bring this user's recent searches
            withRecent: !place.trim(),
            setRecentSearches: saveRecentPlaces,
            limit
        });
    };

    /*
    ============================================================
    ACTIVITY INPUT FOCUS
    ============================================================

    API:

    ?place=Jaipur&activity=

    Backend understands:

    "Give me all activities for Jaipur."

    If no place has been chosen yet, this instead sends:

    ?place=&activity=

    which the backend understands as "give me default/trending
    activities" instead of clearing the list.
    ============================================================
    */

    const handleSpan = () => {
        setSpanDis(false);

        /*
        ALWAYS hide place suggestions
        */

        setAutoCompletePlaceData([]);
        setRecentPlaces([]);

        const limit = window.innerWidth < 768 ? 5 : 10;

        debouncedHandleAutoComplete({
            type,
            place: place.trim(),
            activity: '',
            setAutoCompleteData: setAutoCompleteActivityData,
            // empty activity box: also bring recent activities
            withRecent: !activity.trim(),
            setRecentSearches: saveRecentActivities,
            limit
        });

        inputFo.current?.focus();
    };

    /*
    ============================================================
    ACTIVITY CHANGE
    ============================================================

    Typing:

    scuba

    API:

    ?place=Goa&activity=scuba
    ============================================================
    */

    const handleActivityChange = (value) => {
        setActivity(value);

        /*
        Never show place suggestions while typing activity.
        */

        setAutoCompletePlaceData([]);
        setRecentPlaces([]);
        if (value.trim()) setRecentActivities([]);

        if (!place.trim()) {
            setAutoCompleteActivityData([]);
            return;
        }

        const limit = window.innerWidth < 768 ? 5 : 10;

        debouncedHandleAutoComplete({
            type,
            place: place.trim(),
            activity: value.trim(),
            setAutoCompleteData: setAutoCompleteActivityData,
            limit
        });
    };

    /*
    ============================================================
    SEARCH
    ============================================================
    */

    const handleSearch = (selectedPlace = place, act = activity, opts = {}) => {
        const { clearActivitySuggestions = true } = opts;

        selectedPlace = selectedPlace.trim();
        act = act.trim();

        if (!selectedPlace && !act) {
            return;
        }

        setAutoCompletePlaceData([]);
        setRecentPlaces([]);

        if (clearActivitySuggestions) {
            setAutoCompleteActivityData([]);
            setRecentActivities([]);
        }

        const params = new URLSearchParams();

        if (selectedPlace) {
            params.append('place', selectedPlace);
        }

        if (act) {
            params.append('activity', act);
        }

        navigate(`${resultsBase}/act?${params.toString()}`, {
            state: {
                limit: 9
            }
        });

        if (typeof handleIsSearch === 'function') {
            handleIsSearch(false);
        }
    };

    /*
    ============================================================
    RENDER
    ============================================================
    */

    return (
        <div style={style} className={`holidaySearchWrapper alignCenter ${className}`}>
            {/* =====================================================
                PLACE
            ===================================================== */}

            <div className='activityDiv alignCenter'>
                <img src={Navigation} alt='trvto_navigation' />

                <div style={divStyle} className='relative alignInputWidth'>
                    <input
                        ref={inputPlace}
                        value={place}
                        onFocus={handlePlaceFocus}
                        onClick={handlePlaceFocus}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => handlePlaceChange(e.target.value)}
                        className='holidaySearch'
                        placeholder='Enter the place'
                    />

                    {(autoCompletePlaceData.length > 0 || (!place.trim() && recentPlaces.length > 0)) && (
                        <div ref={autoCompletePlaceRef} className='autoCompleteCon'>
                            {!place.trim() && recentPlaces.length > 0 && (
                                <>
                                    <div className='recentSearchHeader'>Recent places</div>
                                    {recentPlaces.map((name) => (
                                        <div
                                            key={`recent-place-${name}`}
                                            className='recentSearchItem'
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                handlePlaceSelect(name);
                                            }}
                                        >
                                            <span className='recentSearchIcon'>🕘</span>
                                            <span>{name}</span>
                                        </div>
                                    ))}
                                </>
                            )}
                            {(!place.trim() ? withoutRecent(autoCompletePlaceData, recentPlaces) : autoCompletePlaceData).map((item, index) => (
                                <div key={`${item}-${index}`} onClick={() => handlePlaceSelect(item)}>
                                    {item}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* =====================================================
                ACTIVITY
            ===================================================== */}

            <div className='alignCenter'>
                <img src={Navigation} alt='trvto_navigation' />

                <div style={divStyle} className='relative alignInputWidth'>
                    <input
                        ref={inputFo}
                        value={activity}
                        onFocus={handleSpan}
                        onClick={handleSpan}
                        onBlur={() => setSpanDis(true)}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => handleActivityChange(e.target.value)}
                        className='holidaySearch'
                        placeholder=''
                    />

                    {spanDis && !activity && (
                        <TypingAnimation
                            texts={tripText}
                            handleSpan={handleSpan}
                            seConStyle={headerTypingAniStyle.searchContainer}
                        />
                    )}

                    {(autoCompleteActivityData.length > 0 || (!activity.trim() && recentActivities.length > 0)) && (
                        <div ref={autoCompleteActivityRef} className='autoCompleteCon'>
                            {!activity.trim() && recentActivities.length > 0 && (
                                <>
                                    <div className='recentSearchHeader'>Recent activities</div>
                                    {recentActivities.map((name) => (
                                        <div
                                            key={`recent-act-${name}`}
                                            className='recentSearchItem'
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                setActivity(name);
                                                setRecentActivities([]);
                                                setAutoCompleteActivityData([]);
                                                handleSearch(place, name);
                                            }}
                                        >
                                            <span className='recentSearchIcon'>🕘</span>
                                            <span>{name}</span>
                                        </div>
                                    ))}
                                </>
                            )}
                            {(!activity.trim()
                                ? withoutRecent(autoCompleteActivityData, recentActivities)
                                : autoCompleteActivityData
                            ).map((item, index) => (
                                <div
                                    key={`${item}-${index}`}
                                    onClick={() => {
                                        setActivity(item);

                                        setAutoCompleteActivityData([]);

                                        handleSearch(place, item);
                                    }}
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* =====================================================
                SEARCH BUTTON
            ===================================================== */}

            <button type='button' className='hoilidayrSearchBtn' onClick={() => handleSearch()}>
                Search
            </button>

            {/* =====================================================
                LOADING
            ===================================================== */}

            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
        </div>
    );
};

export default Activity;
